import { ElementType, MouseEvent, ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Box } from '@mui/system'
import { useNavigate, useSearchParams } from 'react-router-dom'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import { GridPopper } from './grid-popper.component'
import { IGridPopperListItem } from 'src/types'
import { IFilter } from 'src/components/filter/types/filter.types'

import useStyles from './styles'
import { MoreOutlinedVertIcon } from 'src/assets/icons'
import { StatusBadge } from '../status-badge'
import { FONTS } from 'src/constants'

interface IGridCardProps {
  code?: string
  image?: string
  imageNode?: ElementType | null
  name: string
  route?: string
  description?: string | null
  items?: IGridPopperListItem[]
  imageSubIconNode?: ReactNode
  filter?: IFilter
  status?: boolean
  showStatus?: boolean
  ai_generated?: boolean
}

const GridCard = ({
  code,
  image,
  name,
  route,
  imageNode,
  description,
  items,
  imageSubIconNode,
  filter,
  status,
  showStatus,
  ai_generated = false,
}: IGridCardProps) => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const anchorRefDesktop = useRef<HTMLButtonElement>(null)
  const anchorRefMobile = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const classes = useStyles()
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const [searchParams] = useSearchParams()
  const isFilterOpened = searchParams.get('open')

  const handleMoreClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setOpen((prev) => !prev)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const navigateToSingle = () => {
    navigate(route || '', { state: { filter, layoutFormat: 0, isFilterOpened } })
  }

  const withImage = typeof image !== 'undefined'

  return (
    <>
      <Grid item>
        <Card
          className={classes.cardWrapper}
          sx={{
            pl: !withImage ? 1 : 0,
            bgcolor: ai_generated ? theme.palette.custom.lightGreen : theme.palette.custom.white,
          }}
        >
          <CardActionArea
            className={classes.cardActionArea}
            onClick={route ? navigateToSingle : undefined}
          >
            {withImage && (
              <Box className={classes.cardImageWrapper}>
                <CardMedia
                  component={imageNode ?? 'img'}
                  className={classes.cardImage}
                  image={image}
                  alt={name}
                />
                {imageSubIconNode && (
                  <Box className={classes.cardImageSubIcon}>{imageSubIconNode}</Box>
                )}
                {items && (
                  <IconButton
                    ref={anchorRefDesktop}
                    onClick={handleMoreClick}
                    className={classes.imageButton}
                  >
                    <MoreOutlinedVertIcon />
                  </IconButton>
                )}
                {showStatus && (
                  <Box className={classes.statusIcon}>
                    <StatusBadge
                      label={status ? t('product-public-lable.v') : t('product-not-public-lable.v')}
                      status={!!status}
                    />
                  </Box>
                )}
              </Box>
            )}
            <CardContent className={classes.cardContent}>
              <Typography className={classes.cardName} component="h4">
                {name}
              </Typography>
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Typography className={classes.cardCode} component="p">
                  {code}
                  &nbsp;
                </Typography>
                {isTablet && showStatus && (
                  <Chip
                    sx={{
                      fontWeight: FONTS.WEIGHTS.light,
                      fontSize: FONTS.SIZES.xs,
                      lineHeight: 1.4,
                      height: 'auto',
                      color: status ? 'green' : '#E57373',
                      borderColor: status ? 'green' : '#E57373',
                    }}
                    label={status ? t('product-public-lable.v') : t('product-not-public-lable.v')}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              <Typography
                className={classes.cardDescription}
                data-status={description}
                component="p"
              >
                {description}
              </Typography>
            </CardContent>
            {items && (
              <IconButton
                ref={anchorRefMobile}
                onClick={handleMoreClick}
                className={classes.moreButton}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </CardActionArea>
        </Card>
      </Grid>
      {items && (
        <GridPopper
          items={items}
          handleClose={handleClose}
          open={open}
          anchorRef={isTablet ? anchorRefMobile : anchorRefDesktop}
        />
      )}
    </>
  )
}

export { GridCard }
